/**
 * MyCloudClippingsPage 共享组件
 * 云剪藏簿页面的完整实现，在 Electron 和 Web 端之间共享
 * 直接使用 shared PostCard，通过 platform prop 处理平台差异
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Layout, Spin, Button, Empty, Modal, Input, Checkbox, message } from 'antd';
import { CloudUploadOutlined } from '@ant-design/icons';
import ipEmpty3Image from '../assets/images/ip-empty-3.png';
import { CustomScrollbar } from './CustomScrollbar';
import { ContentHeader } from './ContentHeader';
import { CustomEmpty } from './CustomEmpty';
import { PostCard } from './PostCard';
import { PostList, type GroupByFn } from './PostList';
import { BloggerCard } from './BloggerCard';
import { CloudImportModal } from './CloudImportModal';
import { ImportProgressBar } from './ImportProgressBar';
import { SelectionBox } from './SelectionBox';
import { BatchActionBar } from './BatchActionBar';
import { ClippingsSidebar } from './ClippingsSidebar';
import { CloudShareModal } from './CloudShareModal';
import { EditTagsModal } from './EditTagsModal';
import {
  useMyCloudClippingsPage,
  type CloudClippingsApiClient,
} from '../hooks/useMyCloudClippingsPage';
import { useCloudImport } from '../hooks/useCloudImport';
import { useDragDropTags } from '../hooks/useDragDropTags';
import { useClippingsHeader } from '../hooks/useClippingsHeader';
import { CHROME_EXTENSION_URL } from '../constants';
import type { CloudTag, CloudTagGroup, CloudPost } from '../types';
import type { SortBy } from '../utils/postList';
import { getEffectiveSortBy } from '../utils/viewConfig';

const { Content } = Layout;

// 侧边栏宽度常量
const CLOUD_SIDER_MIN_WIDTH = 248;
const CLOUD_SIDER_MAX_WIDTH = 288;
const CLOUD_SIDER_DEFAULT_WIDTH = 288;
const CLOUD_SIDER_WIDTH_STORAGE_KEY = 'cloudClippings_siderWidth';

// 从 localStorage 读取保存的宽度
const getSavedCloudSiderWidth = (): number => {
  try {
    const saved = localStorage.getItem(CLOUD_SIDER_WIDTH_STORAGE_KEY);
    if (saved) {
      const width = parseInt(saved, 10);
      if (!isNaN(width) && width >= CLOUD_SIDER_MIN_WIDTH && width <= CLOUD_SIDER_MAX_WIDTH) {
        return width;
      }
    }
  } catch (e) {
    // ignore
  }
  return CLOUD_SIDER_DEFAULT_WIDTH;
};

/** PostDetailModal 组件 Props */
export interface PostDetailModalComponentProps {
  post: CloudPost | null;
  visible: boolean;
  onClose: () => void;
  // 评论相关
  isCloudPost?: boolean;
  // 云端帖子操作回调
  onToggleStar?: () => void;
  onDelete?: () => void;
  onEditTags?: () => void;
  isStarred?: boolean;
}

export interface MyCloudClippingsPageProps {
  // 平台必传参数
  isLoggedIn: boolean;
  isInitializing: boolean;
  urlView?: string;
  navigate: (path: string, options?: any) => void;

  // API 客户端
  apiClient: CloudClippingsApiClient;

  // 平台相关配置
  /** 平台类型 */
  platform?: 'web' | 'electron';
  /** 获取本地图片 URL（Electron 专用） */
  getLocalImageUrl?: (imagePath: string) => Promise<{ success: boolean; url?: string }>;
  /** 剪藏到本地回调（Electron 云端帖子专用） */
  onClipToLocal?: (post: CloudPost) => void;
  /** 打开外部 URL（Electron 使用 shell.openExternal，Web 使用 window.open） */
  openExternalUrl?: (url: string) => void;
  /** IPC 调用函数（Electron 专用，用于云端导入） */
  ipcInvoke?: <T>(channel: string, ...args: any[]) => Promise<T>;
  /** 当前登录用户 ID（用于云端更新时的账号校验） */
  userId?: string;

  // 帖子详情弹窗组件（仍需注入，因为平台差异较大）
  PostDetailModalComponent: React.ComponentType<PostDetailModalComponentProps>;
}

/**
 * 按博主分组帖子（与本地剪藏簿行为一致）
 * isPageClip 的帖子放入 'pageclip' 组（显示为"单独剪藏"）
 * 没有 bloggerId 的帖子也放入 'pageclip' 组
 * 其他帖子按博主分组
 */
const groupByTrackedBlogger: GroupByFn = (posts: CloudPost[]): Record<string, CloudPost[]> => {
  const grouped: Record<string, CloudPost[]> = {};
  posts.forEach(post => {
    // 页面剪藏 (isPageClip truthy) 或没有博主的帖子放入 'pageclip' 组
    // 使用 truthy 检查而非严格相等，以匹配本地剪藏簿行为
    const bloggerId = post.isPageClip ? 'pageclip' : (post.bloggerId || 'pageclip');
    if (!grouped[bloggerId]) {
      grouped[bloggerId] = [];
    }
    grouped[bloggerId].push(post);
  });
  return grouped;
};

export const MyCloudClippingsPage: React.FC<MyCloudClippingsPageProps> = ({
  isLoggedIn,
  isInitializing,
  urlView,
  navigate,
  apiClient,
  platform = 'web',
  getLocalImageUrl: _getLocalImageUrl, // 保留接口兼容性，云端帖子不再需要
  onClipToLocal,
  openExternalUrl = (url) => window.open(url, '_blank'),
  ipcInvoke,
  userId,
  PostDetailModalComponent,
}) => {
  const {
    loading,
    loadingContent,
    tags,
    tagGroups,
    bloggers,
    filteredPosts,
    currentView,
    selectedTagId,
    selectedTagGroupId,
    searchKeyword,
    setSearchKeyword,
    sortBy,
    setSortBy,
    expandedTagGroups,
    toggleTagGroupExpand,
    selectedPost,
    setSelectedPost,
    // Pagination
    hasMore,
    isLoadingMore,
    loadMorePosts,
    // Blogger Pagination
    hasMoreBloggers,
    isLoadingMoreBloggers,
    loadMoreBloggers,
    loadAllBloggers,
    totalBloggers,
    // Batch Selection (框选)
    isSelected,
    toggleSelection,
    selectPosts,
    addToSelection,
    clearSelection,
    selectedCount,
    // Tag Actions
    handleCreateTag,
    handleRenameTag,
    handleDeleteTag,
    handleReorderTags,
    handleUpdateTagGroupAssignment,
    handleCreateTagGroup,
    handleRenameTagGroup,
    handleDeleteTagGroup,
    handleReorderTagGroups,
    handleSelectView,
    // Post Actions
    handleDeletePost,
    handleBatchDeletePosts,
    handleToggleStar,
    handleUpdatePostTags,
    refreshPosts,
    // Blogger Actions
    handleDeleteBlogger,
    handleUpdateBloggerTags,
    handleUpdateBloggerAnnotation,
    // Share
    shareModalVisible,
    shareTarget,
    handleOpenShareModal,
    handleCloseShareModal,
    handleShare,
    handleUnshare,
  } = useMyCloudClippingsPage({
    isLoggedIn,
    isInitializing,
    urlView,
    navigate,
    apiClient,
  });

  // ========== 侧栏状态 ==========
  // 侧边栏宽度（使用 useMemo 避免每次渲染都读取 localStorage）
  const initialSiderWidth = useMemo(() => getSavedCloudSiderWidth(), []);

  // ========== 分页加载更多 ==========
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const loadMoreBloggersRef = useRef<HTMLDivElement>(null);

  // IntersectionObserver 监听滚动加载更多
  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;
    if (!loadMoreElement) return;

    // 只有支持分页的页面才启用
    const isPaginatedView = ['recent', 'starred', 'videos', 'uncategorized'].includes(currentView);
    if (!isPaginatedView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          // 延迟 1 秒加载更多（用户要求，便于看到效果）
          setTimeout(() => {
            loadMorePosts();
          }, 1000);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreElement);

    return () => {
      observer.disconnect();
    };
  }, [currentView, hasMore, isLoadingMore, loadMorePosts]);

  // IntersectionObserver 监听博主列表滚动加载更多
  useEffect(() => {
    const loadMoreElement = loadMoreBloggersRef.current;
    if (!loadMoreElement) return;

    // 只有博主视图才启用
    if (currentView !== 'bloggers') return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreBloggers && !isLoadingMoreBloggers) {
          loadMoreBloggers();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreElement);

    return () => {
      observer.disconnect();
    };
  }, [currentView, hasMoreBloggers, isLoadingMoreBloggers, loadMoreBloggers]);

  // 侧边栏宽度调整回调
  const handleSidebarResize = useCallback((width: number) => {
    localStorage.setItem(CLOUD_SIDER_WIDTH_STORAGE_KEY, String(width));
  }, []);

  // Modal 状态
  const [createTagVisible, setCreateTagVisible] = useState(false);
  const [createGroupVisible, setCreateGroupVisible] = useState(false);
  const [renameTagVisible, setRenameTagVisible] = useState(false);
  const [renameGroupVisible, setRenameGroupVisible] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);

  // 帖子标签编辑状态
  const [editingPostTags, setEditingPostTags] = useState<{ postId: string; tagIds: string[] } | null>(null);

  // 博主标签编辑状态
  const [editingBloggerTags, setEditingBloggerTags] = useState<{ bloggerId: string; bloggerName: string; tagIds: string[] } | null>(null);

  // 博主注解编辑状态
  const [editingBloggerAnnotation, setEditingBloggerAnnotation] = useState<{ bloggerId: string; bloggerName: string; annotation: string } | null>(null);

  // ========== 博主列表 Header Hook ==========
  const {
    headerProps: bloggerHeaderProps,
    searchKeyword: bloggerSearchKeyword,
    sortBy: bloggerSortBy,
  } = useClippingsHeader('bloggers');

  // 搜索/排序博主时自动加载全部数据
  useEffect(() => {
    if (currentView === 'bloggers') {
      // 如果有搜索关键词或排序方式不是默认值，需要加载全部博主数据
      if (bloggerSearchKeyword.trim() || bloggerSortBy !== 'time') {
        loadAllBloggers();
      }
    }
  }, [currentView, bloggerSearchKeyword, bloggerSortBy, loadAllBloggers]);

  // ========== 云端导入状态 ==========
  const [importModalVisible, setImportModalVisible] = useState(false);

  // ========== 批量更新博主状态 ==========
  const [isBatchUpdating, setIsBatchUpdating] = useState(false);

  // ========== 框选回调 ==========
  const handleSelectionChange = useCallback((postIds: string[], isAdditive: boolean) => {
    if (isAdditive) {
      addToSelection(postIds);
    } else {
      selectPosts(postIds);
    }
  }, [addToSelection, selectPosts]);

  // 云端导入 Hook（仅 Electron 端可用）
  const cloudImport = useCloudImport({
    isElectron: platform === 'electron' && !!ipcInvoke,
    ipcInvoke,
    onComplete: () => {
      // 导入完成后刷新数据
      // 这里可以通过 apiClient 重新加载数据
    },
  });

  // 是否显示进度条（有正在进行或刚完成的任务）
  const showImportProgress = cloudImport.currentTask && (
    cloudImport.currentTask.status === 'RUNNING' ||
    cloudImport.currentTask.status === 'PAUSED' ||
    cloudImport.currentTask.status === 'COMPLETED' ||
    cloudImport.currentTask.status === 'FAILED'
  );

  // ========== 拖拽 Hook ==========
  const { handlers: dragDropHandlers } = useDragDropTags<CloudTag, CloudTagGroup>({
    tags,
    tagGroups,
    onReorderUngroupedTags: async (tagIds) => {
      const orders = tagIds.map((id, idx) => ({ id, order: idx, groupId: null }));
      handleReorderTags(orders);
    },
    onReorderGroups: async (orders) => {
      handleReorderTagGroups(orders);
    },
    onMoveTagToGroup: async (tagId, groupId, order) => {
      handleUpdateTagGroupAssignment(tagId, groupId, order);
    },
    onReorderTagsInGroup: async (groupId, tagIds) => {
      const orders = tagIds.map((id, idx) => ({ id, order: idx, groupId }));
      handleReorderTags(orders);
    },
    onError: (error, message) => {
      console.error(message, error);
    },
  });

  // ========== 侧边栏回调 ==========

  // 计算选中 key
  const sidebarSelectedKey = useMemo(() => {
    if (currentView === 'recent') return 'recent';
    if (currentView === 'bloggers') return 'bloggers';
    if (currentView === 'uncategorized') return 'uncategorized';
    if (currentView === 'starred') return 'starred';
    if (currentView === 'videos') return 'video';
    if (currentView === 'tag' && selectedTagId) {
      const tag = tags.find(t => t.id === selectedTagId);
      return tag?.name || '';
    }
    if (currentView === 'tagGroup' && selectedTagGroupId) {
      return `group-${selectedTagGroupId}`;
    }
    return 'recent';
  }, [currentView, selectedTagId, selectedTagGroupId, tags]);

  // 展开的组 IDs
  const expandedGroupIds = useMemo(() => {
    return Object.entries(expandedTagGroups)
      .filter(([_, isExpanded]) => isExpanded)
      .map(([id]) => id);
  }, [expandedTagGroups]);

  // 选择处理
  const handleSidebarSelect = useCallback((key: string) => {
    if (key === 'recent') handleSelectView('recent');
    else if (key === 'bloggers') handleSelectView('bloggers');
    else if (key === 'uncategorized') handleSelectView('uncategorized');
    else if (key === 'starred') handleSelectView('starred');
    else if (key === 'video') handleSelectView('videos');
    else if (key.startsWith('group-')) {
      const groupId = key.replace('group-', '');
      handleSelectView('tagGroup', groupId);
    } else {
      // key 是标签名称，需要找到对应的标签 ID
      const tag = tags.find(t => t.name === key);
      if (tag) handleSelectView('tag', tag.id);
    }
  }, [handleSelectView, tags]);

  // Modal 处理函数
  const onCreateTag = async () => {
    if (!newTagName.trim()) return;
    await handleCreateTag(newTagName.trim());
    setNewTagName('');
    setCreateTagVisible(false);
  };

  const onCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    await handleCreateTagGroup(newGroupName.trim());
    setNewGroupName('');
    setCreateGroupVisible(false);
  };

  const onRenameTag = async () => {
    if (!editingTagId || !newTagName.trim()) return;
    await handleRenameTag(editingTagId, newTagName.trim());
    setNewTagName('');
    setEditingTagId(null);
    setRenameTagVisible(false);
  };

  const onRenameGroup = async () => {
    if (!editingGroupId || !newGroupName.trim()) return;
    await handleRenameTagGroup(editingGroupId, newGroupName.trim());
    setNewGroupName('');
    setEditingGroupId(null);
    setRenameGroupVisible(false);
  };

  // 处理排序变更（适配 ContentHeader 的 SortBy 类型）
  const handleSortChange = (newSortBy: SortBy) => {
    // 云剪藏簿只支持 'blogger' 和 'time' 两种排序
    if (newSortBy === 'blogger' || newSortBy === 'time') {
      setSortBy(newSortBy);
    }
  };

  // 批量更新博主（打开博主主页触发 Chrome 扩展自动更新）
  const handleBatchUpdate = useCallback(async () => {
    if (currentView !== 'tag' || !selectedTagId) return;

    // 获取当前 tag 下的博主
    const tagBloggers = bloggers.filter((b) =>
      b.tags?.some((t) => t.id === selectedTagId)
    );

    if (tagBloggers.length === 0) {
      message.warning('该分类下暂无博主');
      return;
    }

    setIsBatchUpdating(true);
    message.info(`开始批量更新 ${tagBloggers.length} 个博主，请勿关闭页面...`);

    // 按顺序打开博主页面，每个间隔 8 秒（等待前一个完成并自动关闭）
    for (let i = 0; i < tagBloggers.length; i++) {
      const blogger = tagBloggers[i];
      if (blogger.profileUrl) {
        // 使用 openExternalUrl 打开主浏览器（Electron 使用 shell.openExternal）
        // 添加 userId 参数用于账号校验
        const hashParams = userId ? `#cloudProfileUpdate&userId=${userId}` : '#cloudProfileUpdate';
        openExternalUrl(`${blogger.profileUrl}${hashParams}`);

        // 等待 8 秒再打开下一个（让前一个完成：2s 页面加载 + 3s 处理 + 3s 关闭）
        if (i < tagBloggers.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 8000));
        }
      }
    }

    message.success(`已完成 ${tagBloggers.length} 个博主的批量更新`);
    setIsBatchUpdating(false);

    // 刷新帖子列表以显示更新后的数据
    await refreshPosts({ silent: true });
  }, [currentView, selectedTagId, bloggers, openExternalUrl, refreshPosts, userId]);

  // 获取当前视图标题
  const getViewTitle = () => {
    switch (currentView) {
      case 'recent':
        return '最近剪藏';
      case 'uncategorized':
        return '未分类';
      case 'starred':
        return '已标星';
      case 'videos':
        return '视频';
      case 'bloggers':
        return '博主';
      case 'tag':
        const tag = tags.find((t) => t.id === selectedTagId);
        return tag?.name || '标签';
      case 'tagGroup':
        const group = tagGroups.find((g) => g.id === selectedTagGroupId);
        return group?.name || '标签组';
      default:
        return '云剪藏簿';
    }
  };

  // 过滤并排序博主列表
  const filteredBloggers = useMemo(() => {
    let result = bloggers;

    // 搜索过滤
    if (bloggerSearchKeyword.trim()) {
      const keyword = bloggerSearchKeyword.toLowerCase().trim();
      result = result.filter((blogger) =>
        blogger.nickname?.toLowerCase().includes(keyword) ||
        blogger.description?.toLowerCase().includes(keyword) ||
        blogger.tags?.some((tag) => tag.name?.toLowerCase().includes(keyword))
      );
    }

    // 根据 bloggerSortBy 排序博主
    result = [...result].sort((a, b) => {
      if (bloggerSortBy === 'blogger') {
        // 按博主粉丝量排序（最多的在前）
        return (b.followersCount || 0) - (a.followersCount || 0);
      } else if (bloggerSortBy === 'time') {
        // 按追踪时间排序（最新追踪的在前）
        const timeA = new Date(a.createdAt || 0).getTime();
        const timeB = new Date(b.createdAt || 0).getTime();
        return timeB - timeA;
      }
      return 0;
    });

    return result;
  }, [bloggers, bloggerSearchKeyword, bloggerSortBy]);

  // ========== 侧边栏额外菜单项 ==========
  const createMenuExtraItems = useMemo(() => {
    if (platform !== 'electron' || !ipcInvoke) return undefined;
    return [
      {
        key: 'import-local',
        icon: <CloudUploadOutlined />,
        label: '导入本地数据',
        disabled: cloudImport.isImporting,
      },
    ];
  }, [platform, ipcInvoke, cloudImport.isImporting]);

  const handleCreateMenuClick = useCallback((key: string) => {
    if (key === 'import-local') {
      setImportModalVisible(true);
    }
  }, []);

  // ========== 侧边栏 header 插槽（仅用于进度条） ==========
  const sidebarHeader = useMemo(() => {
    if (!showImportProgress) return null;
    return (
      <div style={{ padding: '0 12px 12px' }}>
        <ImportProgressBar
          task={cloudImport.currentTask}
          progress={cloudImport.currentProgress}
          onPause={cloudImport.pauseImport}
          onResume={cloudImport.resumeImport}
          onCancel={cloudImport.cancelImport}
          onClose={cloudImport.clearTask}
        />
      </div>
    );
  }, [showImportProgress, cloudImport]);

  // ========== 渲染标签额外内容（帖子数量） ==========
  const renderTagExtra = useCallback((tag: CloudTag) => {
    if (!tag._count?.posts) return null;
    return (
      <span style={{ fontSize: 12, color: 'var(--ant-color-text-quaternary)' }}>
        ({tag._count.posts})
      </span>
    );
  }, []);

  // ========== 条件渲染 ==========

  // 未登录提示
  if (!isInitializing && !isLoggedIn) {
    return (
      <Content
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 16,
          padding: 48,
        }}
      >
        <CustomEmpty image={ipEmpty3Image} description="请先登录以使用云剪藏簿" />
        <Button type="primary" onClick={() => navigate('/auth/login?redirect=/cloud-clippings')}>
          登录
        </Button>
      </Content>
    );
  }

  // 加载中 - 显示侧边栏框架 + 内容区 loading
  if (loading) {
    // 计算 loading 状态下的 selectedKey（简化版，不依赖 tags 数据）
    const loadingSelectedKey = (() => {
      if (currentView === 'recent') return 'recent';
      if (currentView === 'bloggers') return 'bloggers';
      if (currentView === 'uncategorized') return 'uncategorized';
      if (currentView === 'starred') return 'starred';
      if (currentView === 'videos') return 'video';
      if (currentView === 'tagGroup' && selectedTagGroupId) return `group-${selectedTagGroupId}`;
      // tag 视图在 loading 时无法正确显示（因为需要 tags 数据），先显示 recent
      return 'recent';
    })();

    return (
      <>
        {/* 二级侧栏框架 */}
        <ClippingsSidebar<CloudTag, CloudTagGroup>
          tags={[]}
          tagGroups={[]}
          selectedKey={loadingSelectedKey}
          expandedGroupIds={[]}
          onSelect={() => { }}
          onToggleGroup={() => { }}
          onCreateTag={() => { }}
          onCreateGroup={() => { }}
          onRenameTag={() => { }}
          onDeleteTag={() => { }}
          onRenameGroup={() => { }}
          onDeleteGroup={() => { }}
          showInstallExtensionButton={true}
          onInstallExtension={() => openExternalUrl(CHROME_EXTENSION_URL)}
          resizable={true}
          width={initialSiderWidth}
          minWidth={CLOUD_SIDER_MIN_WIDTH}
          maxWidth={CLOUD_SIDER_MAX_WIDTH}
          className="custom-css-scrollbar"
        />
        {/* 内容区 loading */}
        <Content
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Spin size="large" />
        </Content>
      </>
    );
  }

  // ========== 主渲染 ==========

  return (
    <>
      {/* 二级侧栏 - 使用共享 ClippingsSidebar 组件 */}
      <ClippingsSidebar<CloudTag, CloudTagGroup>
        tags={tags}
        tagGroups={tagGroups}
        selectedKey={sidebarSelectedKey}
        expandedGroupIds={expandedGroupIds}
        onSelect={handleSidebarSelect}
        onToggleGroup={toggleTagGroupExpand}
        onCreateTag={() => setCreateTagVisible(true)}
        onCreateGroup={() => setCreateGroupVisible(true)}
        onRenameTag={(tag) => {
          setEditingTagId(tag.id);
          setNewTagName(tag.name);
          setRenameTagVisible(true);
        }}
        onDeleteTag={(tag) => {
          Modal.confirm({
            title: '删除标签',
            content: '确定删除该标签吗？',
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: () => handleDeleteTag(tag.id),
          });
        }}
        onRenameGroup={(group) => {
          setEditingGroupId(group.id);
          setNewGroupName(group.name);
          setRenameGroupVisible(true);
        }}
        onDeleteGroup={(group) => {
          Modal.confirm({
            title: '删除标签组',
            content: '确定删除该标签组吗？组内的标签将变为未分组状态。',
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: () => handleDeleteTagGroup(group.id),
          });
        }}
        dragDrop={dragDropHandlers}
        renderTagExtra={renderTagExtra}
        headerSlot={sidebarHeader}
        showInstallExtensionButton={true}
        onInstallExtension={() => openExternalUrl(CHROME_EXTENSION_URL)}
        createMenuExtraItems={createMenuExtraItems}
        onCreateMenuClick={handleCreateMenuClick}
        resizable={true}
        width={initialSiderWidth}
        minWidth={CLOUD_SIDER_MIN_WIDTH}
        maxWidth={CLOUD_SIDER_MAX_WIDTH}
        onResize={handleSidebarResize}
        className="custom-css-scrollbar"
      />

      {/* 主内容区 */}
      <Content style={{ padding: 0, position: 'relative' }}>
        <CustomScrollbar style={{ height: '100%' }}>
          <SelectionBox
            onSelectionChange={handleSelectionChange}
            enabled={currentView !== 'bloggers'}
            postSelector="[data-post-id]"
          >
            <div style={{ padding: '16px 24px' }}>
              {/* 博主列表视图 */}
              {currentView === 'bloggers' ? (
                <>
                  {/* 博主列表头部 - 使用 useClippingsHeader Hook */}
                  <ContentHeader {...bloggerHeaderProps} />

                  <Spin spinning={loadingContent && filteredBloggers.length === 0}>
                    {filteredBloggers.length === 0 ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 'calc(100vh - 200px)',
                      }}>
                        <CustomEmpty description={bloggerSearchKeyword ? '没有找到匹配的博主' : '暂无追踪的博主'} />
                      </div>
                    ) : (
                      <div>
                        {filteredBloggers.map((blogger) => (
                          <BloggerCard
                            key={blogger.id}
                            blogger={blogger}
                            platform={platform}
                            showUpdateDataButton={true}
                            onEditTags={(b) => {
                              const currentTagIds = b.tags?.map((t) => t.id) || [];
                              setEditingBloggerTags({
                                bloggerId: b.id,
                                bloggerName: b.nickname || '未知博主',
                                tagIds: currentTagIds,
                              });
                            }}
                            onEditAnnotation={(b) => {
                              setEditingBloggerAnnotation({
                                bloggerId: b.id,
                                bloggerName: b.nickname || '未知博主',
                                annotation: b.annotation || '',
                              });
                            }}
                            onUpdateData={(b) => {
                              // 直接打开博主主页并带上 hash 触发自动更新
                              // 添加 userId 参数用于账号校验
                              if (b.profileUrl) {
                                const hashParams = userId ? `#cloudUpdateBlogger&userId=${userId}` : '#cloudUpdateBlogger';
                                window.open(`${b.profileUrl}${hashParams}`, '_blank');
                              }
                            }}
                            onDelete={(b) => {
                              Modal.confirm({
                                title: '确认取消追踪',
                                content: '取消追踪后，该博主的帖子将被删除。确定继续吗？',
                                okText: '确定',
                                okType: 'danger',
                                cancelText: '取消',
                                onOk: () => handleDeleteBlogger(b.id),
                              });
                            }}
                            onOpenProfile={(url) => {
                              window.open(url, '_blank');
                            }}
                          />
                        ))}

                        {/* 加载更多博主触发器 */}
                        {hasMoreBloggers && (
                          <div
                            ref={loadMoreBloggersRef}
                            style={{
                              padding: '16px',
                              textAlign: 'center',
                              color: 'var(--ant-color-text-tertiary)',
                            }}
                          >
                            {isLoadingMoreBloggers ? (
                              <Spin size="small" />
                            ) : (
                              <span>滚动加载更多博主 ({bloggers.length}/{totalBloggers})</span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Spin>
                </>
              ) : (
                <>
                  {/* 帖子列表头部 */}
                  <ContentHeader
                    title={getViewTitle()}
                    searchValue={searchKeyword}
                    onSearchChange={setSearchKeyword}
                    searchPlaceholder="搜索帖子..."
                    sortBy={sortBy}
                    onSortChange={handleSortChange}
                    showSortDropdown={!(currentView === 'recent' || currentView === 'uncategorized' || currentView === 'starred' || currentView === 'videos')}
                    // Share buttons for tag/tagGroup views
                    showShareButtons={currentView === 'tag' || currentView === 'tagGroup'}
                    isShared={(() => {
                      if (currentView === 'tag' && selectedTagId) {
                        const tag = tags.find((t) => t.id === selectedTagId);
                        // 子 tag 继承父级 group 的分享状态
                        if (tag?.groupId) {
                          const parentGroup = tagGroups.find((g) => g.id === tag.groupId);
                          return parentGroup?.isPublic || tag.isPublic || false;
                        }
                        return tag?.isPublic || false;
                      }
                      if (currentView === 'tagGroup' && selectedTagGroupId) {
                        return tagGroups.find((g) => g.id === selectedTagGroupId)?.isPublic || false;
                      }
                      return false;
                    })()}
                    onShare={() => {
                      if (currentView === 'tag' && selectedTagId) {
                        const tag = tags.find((t) => t.id === selectedTagId);
                        if (tag) {
                          // 子 tag 使用父级 group 的分享
                          if (tag.groupId) {
                            const parentGroup = tagGroups.find((g) => g.id === tag.groupId);
                            if (parentGroup) {
                              handleOpenShareModal('cloudTagGroup', parentGroup);
                              return;
                            }
                          }
                          handleOpenShareModal('cloudTag', tag);
                        }
                      } else if (currentView === 'tagGroup' && selectedTagGroupId) {
                        const group = tagGroups.find((g) => g.id === selectedTagGroupId);
                        if (group) handleOpenShareModal('cloudTagGroup', group);
                      }
                    }}
                    onUpdateShare={() => {
                      if (currentView === 'tag' && selectedTagId) {
                        const tag = tags.find((t) => t.id === selectedTagId);
                        if (tag) {
                          // 子 tag 使用父级 group 的分享
                          if (tag.groupId) {
                            const parentGroup = tagGroups.find((g) => g.id === tag.groupId);
                            if (parentGroup) {
                              handleOpenShareModal('cloudTagGroup', parentGroup);
                              return;
                            }
                          }
                          handleOpenShareModal('cloudTag', tag);
                        }
                      } else if (currentView === 'tagGroup' && selectedTagGroupId) {
                        const group = tagGroups.find((g) => g.id === selectedTagGroupId);
                        if (group) handleOpenShareModal('cloudTagGroup', group);
                      }
                    }}
                    // 批量更新博主按钮（仅 Tag 视图）
                    showBatchUpdateButton={currentView === 'tag'}
                    isTag={currentView === 'tag'}
                    onBatchUpdate={currentView === 'tag' ? handleBatchUpdate : undefined}
                    isBatchUpdating={isBatchUpdating}
                  />

                  {/* 帖子列表 - 使用 PostList 组件实现按博主分组 */}
                  <Spin spinning={loadingContent}>
                    {filteredPosts.length === 0 ? (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 'calc(100vh - 200px)',
                        gap: 16,
                      }}>
                        <CustomEmpty
                          description={
                            currentView === 'recent'
                              ? '暂无帖子，去剪藏一些内容吧'
                              : '该分类下暂无帖子'
                          }
                        />
                        {currentView === 'recent' && (
                          <button
                            className="gradient-button"
                            style={{ flex: 'none', width: 'auto' }}
                            onClick={() => openExternalUrl(CHROME_EXTENSION_URL)}
                          >
                            安装扩展进行剪藏
                          </button>
                        )}
                      </div>
                    ) : (
                      <PostList
                        posts={filteredPosts}
                        sortBy={getEffectiveSortBy(currentView, sortBy)}
                        emptyText="暂无帖子"
                        groupBy={groupByTrackedBlogger}
                        sortPageClips={true}
                        isSelected={isSelected}
                        onBloggerClick={(blogger) => {
                          if (blogger.profileUrl) {
                            window.open(blogger.profileUrl, '_blank');
                          }
                        }}
                        onEditBloggerTags={(blogger) => {
                          const currentTagIds = blogger.tags?.map((t: any) => t.id) || [];
                          setEditingBloggerTags({
                            bloggerId: blogger.id,
                            bloggerName: blogger.nickname || '未知博主',
                            tagIds: currentTagIds,
                          });
                        }}
                        showBloggerEditButton={true}
                        renderPostCard={({ post }) => {
                          return (
                            <PostCard
                              post={post}
                              onClick={() => setSelectedPost(post)}
                              platform={platform}
                              isCloudPost={true}
                              onClipToLocal={onClipToLocal}
                              // 完整剪藏：打开帖子页面触发插件自动剪藏（Web 和 Electron 通用）
                              onFullClip={post.isPreview ? async () => {
                                let fullUrl = post.postUrl;
                                if (!fullUrl?.startsWith('http')) {
                                  fullUrl = `https://www.xiaohongshu.com${fullUrl?.startsWith('/') ? '' : '/'}${fullUrl}`;
                                }
                                // 添加 userId 参数用于账号校验
                                const hashParams = userId ? `#cloudAutoClip&userId=${userId}` : '#cloudAutoClip';
                                openExternalUrl(`${fullUrl}${hashParams}`);
                                // 等待 8 秒让浏览器完成剪藏
                                await new Promise(resolve => setTimeout(resolve, 8000));
                                // 静默刷新帖子列表（不显示加载蒙层）
                                await refreshPosts({ silent: true });
                              } : undefined}
                              // Batch Selection
                              isSelected={isSelected(post.id)}
                              onToggleSelection={toggleSelection}
                              // Post Actions
                              onToggleStar={() => handleToggleStar(post.id)}
                              onDelete={() => handleDeletePost(post.id)}
                              onEditTags={() => {
                                const currentTagIds = post.tags?.map((t: any) => t.id) || [];
                                setEditingPostTags({ postId: post.id, tagIds: currentTagIds });
                              }}
                            />
                          );
                        }}
                      />
                    )}

                    {/* 底部提示：支持分页的页面 - 加载更多触发器 */}
                    {(currentView === 'recent' || currentView === 'starred' || currentView === 'videos' || currentView === 'uncategorized') && filteredPosts.length > 0 && (
                      <div
                        ref={loadMoreRef}
                        style={{
                          textAlign: 'center',
                          padding: '24px 0',
                          color: 'var(--ant-color-text-tertiary)',
                          fontSize: 13,
                        }}
                      >
                        {isLoadingMore ? (
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                            <Spin size="small" />
                            <span>加载中...</span>
                          </div>
                        ) : hasMore ? (
                          <span>滑动到底部加载更多</span>
                        ) : currentView === 'recent' && filteredPosts.length >= 150 ? (
                          <span>已加载最近 {filteredPosts.length} 条记录</span>
                        ) : (
                          <span>已加载全部 {filteredPosts.length} 条记录</span>
                        )}
                      </div>
                    )}
                  </Spin>
                </>
              )}
            </div>
          </SelectionBox>
        </CustomScrollbar>
      </Content>

      {/* 帖子详情弹窗 */}
      <PostDetailModalComponent
        post={selectedPost}
        visible={!!selectedPost}
        onClose={() => setSelectedPost(null)}
        isCloudPost={true}
        isStarred={selectedPost?.isStarred}
        onToggleStar={selectedPost ? async () => {
          await handleToggleStar(selectedPost.id);
        } : undefined}
        onDelete={selectedPost ? async () => {
          await handleDeletePost(selectedPost.id);
          setSelectedPost(null);
        } : undefined}
        onEditTags={selectedPost ? () => {
          const currentTagIds = selectedPost.tags?.map(t => t.id) || [];
          setEditingPostTags({ postId: selectedPost.id, tagIds: currentTagIds });
        } : undefined}
      />

      {/* ========== Modals ========== */}

      {/* 创建标签 Modal */}
      <Modal
        title="创建标签"
        open={createTagVisible}
        onOk={onCreateTag}
        onCancel={() => {
          setCreateTagVisible(false);
          setNewTagName('');
        }}
        okText="创建"
        cancelText="取消"
      >
        <Input
          placeholder="输入标签名称"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          onPressEnter={onCreateTag}
          autoFocus
        />
      </Modal>

      {/* 创建标签组 Modal */}
      <Modal
        title="创建标签组"
        open={createGroupVisible}
        onOk={onCreateGroup}
        onCancel={() => {
          setCreateGroupVisible(false);
          setNewGroupName('');
        }}
        okText="创建"
        cancelText="取消"
      >
        <Input
          placeholder="输入标签组名称"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onPressEnter={onCreateGroup}
          autoFocus
        />
      </Modal>

      {/* 重命名标签 Modal */}
      <Modal
        title="重命名标签"
        open={renameTagVisible}
        onOk={onRenameTag}
        onCancel={() => {
          setRenameTagVisible(false);
          setNewTagName('');
          setEditingTagId(null);
        }}
        okText="确定"
        cancelText="取消"
      >
        <Input
          placeholder="输入新名称"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          onPressEnter={onRenameTag}
          autoFocus
        />
      </Modal>

      {/* 重命名标签组 Modal */}
      <Modal
        title="重命名标签组"
        open={renameGroupVisible}
        onOk={onRenameGroup}
        onCancel={() => {
          setRenameGroupVisible(false);
          setNewGroupName('');
          setEditingGroupId(null);
        }}
        okText="确定"
        cancelText="取消"
      >
        <Input
          placeholder="输入新名称"
          value={newGroupName}
          onChange={(e) => setNewGroupName(e.target.value)}
          onPressEnter={onRenameGroup}
          autoFocus
        />
      </Modal>

      {/* 编辑帖子标签 Modal (使用共有组件) */}
      <EditTagsModal
        visible={!!editingPostTags}
        title="编辑帖子标签"
        initialTags={(() => {
          // IDs -> Names
          if (!editingPostTags) return [];
          return editingPostTags.tagIds
            .map(id => tags.find(t => t.id === id)?.name)
            .filter(Boolean) as string[];
        })()}
        allTags={tags.map(t => t.name)}
        onClose={() => setEditingPostTags(null)}
        onSave={async (newTagNames) => {
          if (!editingPostTags) return;

          try {
            // 1. Identify existing tag IDs
            const existingTagIds: string[] = [];
            const newTagsToCreate: string[] = [];

            newTagNames.forEach(name => {
              const foundTag = tags.find(t => t.name === name);
              if (foundTag) {
                existingTagIds.push(foundTag.id);
              } else {
                newTagsToCreate.push(name);
              }
            });

            // 2. Create new tags if any
            for (const name of newTagsToCreate) {
              // We need createTag to return the ID or object, but handleCreateTag might be void or just refresh.
              // handleCreateTag calls apiClient.createTag which returns the tag.
              // But hook handleCreateTag wrapper might not return it.
              // Inspecting useMyCloudClippingsPage hook usage: simple wrapper.
              // We should probably rely on filteredPosts/tags refresh or use apiClient directly here?
              // But using handleCreateTag ensures state update consistency.
              // We might need to optimistically guess or fetch fresh tags.
              // Actually the simplest way: call create, wait, then (ideally) we need the ID.
              // Since existing handleCreateTag just refreshes, let's use apiClient directly if exposed?
              // The hook exposes handleCreateTag. Let's assume it returns possibly.
              // Wait, I can't modify the hook right now easily without checking it.
              // But wait, apiClient IS passed to this component. usage: apiClient.createTag(name)

              const res = await apiClient.createTag({ name });
              if (res.tag) {
                existingTagIds.push(res.tag.id);
              }
            }

            // 3. Update post tags
            await handleUpdatePostTags(editingPostTags.postId, existingTagIds);

            // Refresh tags if we created some (handleUpdatePostTags refreshes posts, maybe tags too?)
            // If we created tags via apiClient directly, we should trigger a refresh of tags list
            // or rely on handleUpdatePostTags to refresh everything (usually it refreshes posts list).
            // But side bar tags might be outdated if we don't refresh tags.
            // Let's assume handleUpdatePostTags refreshes necessary data or we can force refresh.
            // Actually handleCreateTag does refresh.
            if (newTagsToCreate.length > 0) {
              // Trigger refresh if needed, but handleUpdatePostTags calls refreshPosts.
              // We might want to refresh tags specifically if they are separate.
              // In useMyCloudClippingsPage, data loading loads everything usually.
            }
          } catch (error) {
            console.error('Failed to update tags:', error);
            message.error('更新标签失败');
            throw error; // Rethrow to stop modal spinner if any
          }
        }}
      />

      {/* 编辑博主标签 Modal (使用共有组件) */}
      <EditTagsModal
        visible={!!editingBloggerTags}
        title={`编辑标签 - ${editingBloggerTags?.bloggerName || ''}`}
        initialTags={(() => {
          // IDs -> Names
          if (!editingBloggerTags) return [];
          return editingBloggerTags.tagIds
            .map(id => tags.find(t => t.id === id)?.name)
            .filter(Boolean) as string[];
        })()}
        allTags={tags.map(t => t.name)}
        onClose={() => setEditingBloggerTags(null)}
        onSave={async (newTagNames) => {
          if (!editingBloggerTags) return;

          try {
            // 1. Identify existing tag IDs
            const existingTagIds: string[] = [];
            const newTagsToCreate: string[] = [];

            newTagNames.forEach(name => {
              const foundTag = tags.find(t => t.name === name);
              if (foundTag) {
                existingTagIds.push(foundTag.id);
              } else {
                newTagsToCreate.push(name);
              }
            });

            // 2. Create new tags if any
            for (const name of newTagsToCreate) {
              const res = await apiClient.createTag({ name });
              if (res.tag) {
                existingTagIds.push(res.tag.id);
              }
            }

            // 3. Update blogger tags
            await handleUpdateBloggerTags(editingBloggerTags.bloggerId, existingTagIds);
            setEditingBloggerTags(null);

          } catch (error) {
            console.error('Failed to update blogger tags:', error);
            message.error('更新博主标签失败');
            throw error;
          }
        }}
      />

      {/* 编辑博主备注 Modal */}
      <Modal
        title={`编辑备注 - ${editingBloggerAnnotation?.bloggerName || ''}`}
        open={!!editingBloggerAnnotation}
        onOk={async () => {
          if (editingBloggerAnnotation) {
            await handleUpdateBloggerAnnotation(editingBloggerAnnotation.bloggerId, editingBloggerAnnotation.annotation);
            setEditingBloggerAnnotation(null);
          }
        }}
        onCancel={() => setEditingBloggerAnnotation(null)}
        okText="确定"
        cancelText="取消"
      >
        <Input.TextArea
          value={editingBloggerAnnotation?.annotation || ''}
          onChange={(e) => {
            if (editingBloggerAnnotation) {
              const value = e.target.value.slice(0, 60);
              setEditingBloggerAnnotation({
                ...editingBloggerAnnotation,
                annotation: value,
              });
            }
          }}
          placeholder="添加备注（最多60字）"
          maxLength={60}
          rows={2}
          style={{ resize: 'none' }}
        />
      </Modal>

      {/* 云端导入 Modal（Electron 专用） */}
      {platform === 'electron' && ipcInvoke && (
        <CloudImportModal
          visible={importModalVisible}
          onClose={() => setImportModalVisible(false)}
          onComplete={() => {
            // 导入完成后可以刷新数据
            setImportModalVisible(false);
          }}
          ipcInvoke={ipcInvoke}
        />
      )}

      {/* 批量操作栏 */}
      <BatchActionBar
        selectedCount={selectedCount}
        onDelete={handleBatchDeletePosts}
        onClearSelection={clearSelection}
        visible={selectedCount > 0}
      />

      {/* 分享弹窗 */}
      <CloudShareModal
        visible={shareModalVisible}
        onCancel={handleCloseShareModal}
        type={shareTarget?.type || 'cloudTag'}
        itemId={shareTarget?.id || ''}
        itemName={shareTarget?.name || ''}
        isPublic={shareTarget?.isPublic || false}
        currentAlias={shareTarget?.alias}
        currentDescription={shareTarget?.description}
        currentIsAnonymous={shareTarget?.isAnonymous}
        currentIsEncrypted={shareTarget?.isEncrypted}
        onShare={handleShare}
        onUnshare={shareTarget?.isPublic ? handleUnshare : undefined}
      />
    </>
  );
};

export default MyCloudClippingsPage;
